"use client";

import { ArrowRight, Building2, Signature as FileSignature, Layers, ShieldCheck, Activity, Zap, Thermometer, HardHat, Flame, Droplets, Search, ClipboardCheck } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Engineering() {
  const intro = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const grid = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const pillars = [{
    icon: ShieldCheck,
    title: "Fire Suppression Systems",
    body: "Specialized design of water, foam, and gaseous suppression systems for high-hazard industrial environments and critical assets."
  }, {
    icon: Flame,
    title: "Fire Detection & Alarm",
    body: "Advanced early-warning systems utilizing flame, gas, and smoke detection technologies integrated for rapid industrial response."
  }, {
    icon: Layers,
    title: "Passive Fire Protection",
    body: "Engineering specifications for structural fireproofing, fire-rated barriers, and penetration seals to maintain building integrity."
  }, {
    icon: Activity,
    title: "Fire & Explosion Modeling",
    body: "Computational Fluid Dynamics (CFD) and consequence modeling to predict thermal radiation and overpressure impacts."
  }, {
    icon: Search,
    title: "Code Consulting",
    body: "Expert navigation of NFPA, IBC, and international fire codes to ensure project compliance from design through construction."
  }, {
    icon: ClipboardCheck,
    title: "Process Hazard Analysis",
    body: "Systematic technical reviews including HAZOP and LOPA to identify and mitigate fire risks within complex chemical processes."
  }, {
    icon: Droplets,
    title: "Hydraulic Analysis",
    body: "Detailed water supply evaluations and hydraulic calculations to verify firewater system performance under peak demand."
  }, {
    icon: FileSignature,
    title: "Third-Party Review",
    body: "Independent engineering verification of fire protection designs to satisfy AHJ requirements and insurance underwriting standards."
  }, {
    icon: HardHat,
    title: "Commissioning & Testing",
    body: "On-site functional testing and integrated system commissioning to ensure life safety systems operate as engineered."
  }];
  return <section data-section-id="15843" id="engineering-solutions" className="bg-background text-foreground py-24 md:py-32">
      <div className="container mx-auto px-6 md:px-10 lg:px-16 max-w-[1280px]">
        
        <div ref={intro.ref} className={`mx-auto max-w-[44rem] text-center transition-all duration-700 ease-out ${intro.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
        transitionTimingFunction: "cubic-bezier(0.16, 1, 0.3, 1)"
      }}>
          <p className="font-default text-xs font-bold uppercase" style={{
          color: "hsl(240 100% 25%)",
          letterSpacing: "0.15em"
        }}>
            Technical Expertise
          </p>
          <h2 className="font-serif font-normal text-foreground mt-5" style={{
          fontSize: "clamp(2rem, calc(2.2vw + 1rem), 3.25rem)",
          letterSpacing: "-0.018em",
          lineHeight: 1.08
        }}>
            Engineering resilience for <em className="italic font-normal">critical</em> infrastructure
          </h2>
          
          <div className="mx-auto mt-6" style={{
          width: "64px",
          height: "1px",
          backgroundColor: "hsl(240 100% 25%)"
        }} aria-hidden="true" />
        </div>

        
        <div ref={grid.ref} className="mt-16 md:mt-20">
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {pillars.map((pillar, i) => {
            const Icon = pillar.icon;
            return <div key={i} data-index={i} className={`transition-all ease-out ${grid.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
              transitionDuration: "700ms",
              transitionDelay: `${i * 80}ms`,
              transitionTimingFunction: "cubic-bezier(0.16, 1, 0.3, 1)"
            }}>
                  <Card className="group bg-card border border-secondary p-10 h-full transition-all duration-[260ms] ease-out hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-lg gap-0">
                    <div className="flex flex-col h-full">
                      <Icon className="size-6 text-primary" strokeWidth={1.25} aria-hidden="true" />
                      <h3 className="font-default text-xl font-semibold text-foreground mt-8 tracking-tight">
                        {pillar.title}
                      </h3>
                      <div className="mt-5 bg-secondary" style={{
                    width: "32px",
                    height: "1px"
                  }} aria-hidden="true" />
                      <p className="font-default text-base font-normal text-muted-foreground mt-5 flex-1" style={{
                    lineHeight: 1.65
                  }}>
                        {pillar.body}
                      </p>
                    </div>
                  </Card>
                </div>;
          })}
          </div>

          
          <div className={`mt-6 transition-all ease-out ${grid.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
          transitionDuration: "700ms",
          transitionDelay: "240ms",
          transitionTimingFunction: "cubic-bezier(0.16, 1, 0.3, 1)"
        }}>
            <Card className="group bg-card border border-secondary p-0 overflow-hidden transition-all duration-[260ms] ease-out hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-lg gap-0">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-0">
                <div className="lg:col-span-7 p-10 md:p-12 flex flex-col">
                  <Building2 className="size-6 text-primary" strokeWidth={1.25} aria-hidden="true" />
                  <h3 className="font-default text-xl font-semibold text-foreground mt-8 tracking-tight">
                    Industrial Risk Management
                  </h3>
                  <div className="mt-5 bg-secondary" style={{
                  width: "32px",
                  height: "1px"
                }} aria-hidden="true" />
                  <p className="font-default text-base font-normal text-muted-foreground mt-5 max-w-[44rem]" style={{
                  lineHeight: 1.65
                }}>
                    Comprehensive risk mitigation strategies for large-scale industrial operations. We provide the technical foundation for insurance underwriting, risk modeling, and safety engineering across oil & gas, chemical, LNG, power generation, renewable energy, mining, and commercial sectors.
                  </p>
                  <div className="mt-8">
                    <Link to="#" className="inline-flex items-center gap-1.5 font-default text-sm font-medium text-primary underline underline-offset-[6px] decoration-1 transition-colors duration-[180ms] hover:text-accent-foreground" style={{
                    textDecorationColor: "hsl(240 100% 25%)"
                  }}>
                      View technical scope
                      <ArrowRight className="size-4" strokeWidth={1.25} />
                    </Link>
                  </div>
                </div>
                <div className="lg:col-span-5 relative min-h-[240px] lg:min-h-[320px] overflow-hidden">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1705042/img/industrial_facility_safety.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1705042/img/industrial_facility_safety.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1705042/img/industrial_facility_safety.webp-wvc-sizes" alt="Industrial facility safety infrastructure" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
                </div>
              </div>
            </Card>
          </div>
        </div>

        
        <div className={`mt-16 flex justify-center transition-all duration-700 ease-out ${grid.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
        transitionDelay: "400ms",
        transitionTimingFunction: "cubic-bezier(0.16, 1, 0.3, 1)"
      }}>
          <Link to="/ServicesPage">
            <Button variant="outline" className="h-auto px-7 py-3.5 border border-primary bg-primary text-primary-foreground font-default text-sm font-bold hover:bg-accent-foreground hover:text-white transition-colors duration-[220ms]" style={{
            letterSpacing: "0.05em"
          }}>
              VIEW ALL CAPABILITIES
            </Button>
          </Link>
        </div>
      </div>
    </section>;
}